import { useState, useRef } from "react";
import "./EmployeePartnerForm.css";

const STEPS = [
  { label: "Personal", icon: "👤" },
  { label: "Professional", icon: "💼" },
  { label: "Partnership", icon: "🤝" },
];

const OPTS = {
  yearsOfExperience: ["0-2 Years", "3-5 Years", "6-10 Years", "10+ Years"],
  expertise: [
    "AI & Machine Learning",
    "Software Development",
    "Cloud Computing",
    "Data Science",
    "DevOps",
    "Cybersecurity",
    "Product Management",
    "Digital Transformation"
  ],
  interestArea: [
    "Full-time Employment",
    "Contract/Consulting",
    "Part-time Advisory",
    "Freelance Projects",
    "Research Collaboration"
  ],
  availability: ["Immediate", "Within 1 Month", "Within 3 Months", "Exploring Opportunities"],
  expectedCompensation: ["₹10-20 LPA", "₹20-30 LPA", "₹30-50 LPA", "₹50+ LPA", "Negotiable"],
  workMode: ["Remote", "Hybrid", "On-site", "Flexible"],
};

export default function EmployeePartnerForm() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    fullName: "", contactNumber: "", email: "", currentRole: "",
    currentCompany: "", linkedin: "", resume: null,
    yearsOfExperience: "", expertise: [], skills: [],
    interestArea: "", availability: "", expectedCompensation: "",
    preferredLocation: "", workMode: "Flexible", portfolio: "",
    github: "", additionalNotes: ""
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileRef = useRef();

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleMulti = (k, v) => set(k, form[k].includes(v) ? form[k].filter(x => x !== v) : [...form[k], v]);

  // Separate validation function for Step 0
  const validateStep0 = () => {
    const e = {};
    if (!form.fullName.trim()) e.fullName = "Required";
    if (!form.contactNumber.trim()) e.contactNumber = "Required";
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = "Valid email required";
    if (!form.currentRole.trim()) e.currentRole = "Required";
    return e;
  };

  // Separate validation function for Step 1
  const validateStep1 = () => {
    const e = {};
    if (!form.yearsOfExperience) e.yearsOfExperience = "Required";
    if (!form.expertise.length) e.expertise = "Select at least one";
    if (!form.skills.length) e.skills = "Add at least one skill";
    return e;
  };

  // Separate validation function for Step 2
  const validateStep2 = () => {
    const e = {};
    if (!form.interestArea) e.interestArea = "Required to fill";
    if (!form.availability) e.availability = "Required";
    return e;
  };

  const next = () => {
    let validationErrors = {};
    
    // Validate only the current step
    if (step === 0) {
      validationErrors = validateStep0();
    } else if (step === 1) {
      validationErrors = validateStep1();
    }
    
    if (Object.keys(validationErrors).length === 0) {
      setStep(s => s + 1);
      setErrors({}); // Clear errors when moving to next step
    } else {
      setErrors(validationErrors);
    }
  };
  
  const back = () => setStep(s => s - 1);

  const submitToBackend = async (formData) => {
    setIsSubmitting(true);
    const submitForm = new FormData();
    
    submitForm.append('fullName', formData.fullName);
    submitForm.append('contactNumber', formData.contactNumber);
    submitForm.append('email', formData.email);
    submitForm.append('currentRole', formData.currentRole);
    submitForm.append('currentCompany', formData.currentCompany);
    submitForm.append('linkedin', formData.linkedin);
    submitForm.append('yearsOfExperience', formData.yearsOfExperience);
    submitForm.append('expertise', JSON.stringify(formData.expertise));
    submitForm.append('skills', JSON.stringify(formData.skills));
    submitForm.append('interestArea', formData.interestArea);
    submitForm.append('availability', formData.availability);
    submitForm.append('expectedCompensation', formData.expectedCompensation);
    submitForm.append('preferredLocation', formData.preferredLocation);
    submitForm.append('workMode', formData.workMode);
    submitForm.append('portfolio', formData.portfolio);
    submitForm.append('github', formData.github);
    submitForm.append('additionalNotes', formData.additionalNotes);
    
    if (formData.resume) {
      submitForm.append('resume', formData.resume);
    }
    
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/employee-partner/submit`, {
        method: 'POST',
        body: submitForm
      });
      
      const result = await response.json();
      
      if (result.success) {
        setSubmitted(true);
      } else {
        const validationErrors = {};
        if (result.errors) {
          result.errors.forEach(err => {
            validationErrors[err.param] = err.msg;
          });
        }
        setErrors(validationErrors);
        alert(result.message || 'Please fix the errors in the form');
      }
    } catch (error) {
      console.error('Submission error:', error);
      alert('Failed to submit application. Please make sure the backend server is running.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    // Validate only step 2
    const validationErrors = validateStep2();
    
    if (Object.keys(validationErrors).length === 0) {
      submitToBackend(form);
    } else {
      setErrors(validationErrors);
    }
  };

  if (submitted) return (
    <div className="epf epf--success">
      <div className="epf__success-icon">🌟</div>
      <h2>Application Submitted Successfully!</h2>
      <p>Thank you, <strong>{form.fullName}</strong>! Your employee partner application has been received. Our HR team will review your profile and get back to you within 5-7 business days.</p>
      <button className="epf__btn epf__btn--primary" onClick={() => {
        setSubmitted(false);
        setStep(0);
        setForm({
          fullName: "", contactNumber: "", email: "", currentRole: "",
          currentCompany: "", linkedin: "", resume: null,
          yearsOfExperience: "", expertise: [], skills: [],
          interestArea: "", availability: "", expectedCompensation: "",
          preferredLocation: "", workMode: "Flexible", portfolio: "",
          github: "", additionalNotes: ""
        });
        setErrors({});
      }}>Submit Another Application</button>
    </div>
  );

  return (
    <div className="epf">
      <div className="epf__header">
        <span className="epf__eyebrow">Join Our Team</span>
        <h1 className="epf__title">Employee Partner <span>Application</span></h1>
        <p className="epf__subtitle">Become a part of Bairees' growing team of innovators and technology experts.</p>
      </div>

      <div className="epf__stepper">
        {STEPS.map((s, i) => (
          <div key={i} className={`epf__step ${i === step ? "epf__step--active" : ""} ${i < step ? "epf__step--done" : ""}`}>
            <div className="epf__step-circle">{i < step ? "✓" : s.icon}</div>
            <span className="epf__step-label">{s.label}</span>
            {i < 2 && <div className={`epf__step-line ${i < step ? "epf__step-line--done" : ""}`} />}
          </div>
        ))}
      </div>

      <form className="epf__form" onSubmit={submit} noValidate>
        {/* STEP 0 - Personal Information */}
        {step === 0 && (
          <div className="epf__panel">
            <h3 className="epf__panel-title">Personal Information</h3>
            <div className="epf__grid epf__grid--2">
              <F label="Full Name *" error={errors.fullName}>
                <input 
                  className={`epf__input ${errors.fullName ? "epf__input--err" : ""}`} 
                  value={form.fullName} 
                  onChange={e => set("fullName", e.target.value)} 
                  placeholder="Rahul Sharma" 
                />
              </F>
              <F label="Contact Number *" error={errors.contactNumber}>
                <input 
                  className={`epf__input ${errors.contactNumber ? "epf__input--err" : ""}`} 
                  value={form.contactNumber} 
                  onChange={e => set("contactNumber", e.target.value)} 
                  placeholder="+91 98765 43210" 
                />
              </F>
            </div>
            <F label="Email Address *" error={errors.email}>
              <input 
                className={`epf__input ${errors.email ? "epf__input--err" : ""}`} 
                type="email" 
                value={form.email} 
                onChange={e => set("email", e.target.value)} 
                placeholder="rahul.sharma@example.com" 
              />
            </F>
            <div className="epf__grid epf__grid--2">
              <F label="Current Role / Designation *" error={errors.currentRole}>
                <input 
                  className={`epf__input ${errors.currentRole ? "epf__input--err" : ""}`} 
                  value={form.currentRole} 
                  onChange={e => set("currentRole", e.target.value)} 
                  placeholder="Senior Software Engineer" 
                />
              </F>
              <F label="Current Company">
                <input 
                  className="epf__input" 
                  value={form.currentCompany} 
                  onChange={e => set("currentCompany", e.target.value)} 
                  placeholder="Tech Corp India" 
                />
              </F>
            </div>
            <F label="LinkedIn Profile URL">
              <input 
                className="epf__input" 
                value={form.linkedin} 
                onChange={e => set("linkedin", e.target.value)} 
                placeholder="https://linkedin.com/in/rahulsharma" 
              />
            </F>
            <F label="Upload Resume / CV">
              <div className="epf__file" onClick={() => fileRef.current.click()}>
                <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" hidden onChange={e => set("resume", e.target.files[0])} />
                <div className="epf__file-icon">📄</div>
                <div>
                  <div className="epf__file-main">{form.resume ? form.resume.name : "Upload your resume (PDF, DOC, DOCX)"}</div>
                  <div className="epf__file-hint">Max file size: 5MB</div>
                </div>
              </div>
            </F>
          </div>
        )}

        {/* STEP 1 - Professional Background */}
        {step === 1 && (
          <div className="epf__panel">
            <h3 className="epf__panel-title">Professional Background</h3>
            <F label="Years of Experience *" error={errors.yearsOfExperience}>
              <div className="epf__radio-group">
                {OPTS.yearsOfExperience.map(o => (
                  <label key={o} className={`epf__radio ${form.yearsOfExperience === o ? "epf__radio--active" : ""}`}>
                    <input 
                      type="radio" 
                      name="yearsOfExperience" 
                      value={o} 
                      checked={form.yearsOfExperience === o} 
                      onChange={() => set("yearsOfExperience", o)} 
                    />
                    {o}
                  </label>
                ))}
              </div>
            </F>
            <F label="Core Expertise Areas * (select all that apply)" error={errors.expertise}>
              <div className="epf__chip-group">
                {OPTS.expertise.map(o => (
                  <button 
                    key={o} 
                    type="button" 
                    className={`epf__chip ${form.expertise.includes(o) ? "epf__chip--active" : ""}`} 
                    onClick={() => toggleMulti("expertise", o)}
                  >
                    {o}
                  </button>
                ))}
              </div>
            </F>
            <F label="Technical Skills * (add comma-separated skills)" error={errors.skills}>
              <input 
                className="epf__input" 
                value={form.skills.join(", ")} 
                onChange={e => set("skills", e.target.value.split(",").map(s => s.trim()).filter(s => s))} 
                placeholder="React, Node.js, Python, AWS, MongoDB" 
              />
              <small className="epf__hint">Enter skills separated by commas</small>
            </F>
            <div className="epf__grid epf__grid--2">
              <F label="Portfolio / Personal Website">
                <input 
                  className="epf__input" 
                  value={form.portfolio} 
                  onChange={e => set("portfolio", e.target.value)} 
                  placeholder="https://yourportfolio.com" 
                />
              </F>
              <F label="GitHub Profile">
                <input 
                  className="epf__input" 
                  value={form.github} 
                  onChange={e => set("github", e.target.value)} 
                  placeholder="https://github.com/username" 
                />
              </F>
            </div>
          </div>
        )}

        {/* STEP 2 - Partnership Preferences */}
        {step === 2 && (
          <div className="epf__panel">
            <h3 className="epf__panel-title">Partnership Preferences</h3>
            <F label="Type of Partnership Interest *" error={errors.interestArea}>
              <div className="epf__select-group">
                {OPTS.interestArea.map(o => (
                  <label 
                    key={o} 
                    className={`epf__select-card ${form.interestArea === o ? "epf__select-card--active" : ""}`}
                  >
                    <input 
                      type="radio" 
                      name="interestArea" 
                      value={o} 
                      checked={form.interestArea === o} 
                      onChange={() => set("interestArea", o)} 
                    />
                    {o}
                  </label>
                ))}
              </div>
            </F>
            <div className="epf__grid epf__grid--2">
              <F label="Availability to Join *" error={errors.availability}>
                <div className="epf__radio-group">
                  {OPTS.availability.map(o => (
                    <label key={o} className={`epf__radio ${form.availability === o ? "epf__radio--active" : ""}`}>
                      <input 
                        type="radio" 
                        name="availability" 
                        value={o} 
                        checked={form.availability === o} 
                        onChange={() => set("availability", o)} 
                      />
                      {o}
                    </label>
                  ))}
                </div>
              </F>
              <F label="Expected Compensation">
                <div className="epf__radio-group">
                  {OPTS.expectedCompensation.map(o => (
                    <label key={o} className={`epf__radio ${form.expectedCompensation === o ? "epf__radio--active" : ""}`}>
                      <input 
                        type="radio" 
                        name="expectedCompensation" 
                        value={o} 
                        checked={form.expectedCompensation === o} 
                        onChange={() => set("expectedCompensation", o)} 
                      />
                      {o}
                    </label>
                  ))}
                </div>
              </F>
            </div>
            <div className="epf__grid epf__grid--2">
              <F label="Preferred Location">
                <input 
                  className="epf__input" 
                  value={form.preferredLocation} 
                  onChange={e => set("preferredLocation", e.target.value)} 
                  placeholder="Bangalore, Remote, etc." 
                />
              </F>
              <F label="Work Mode Preference">
                <div className="epf__radio-group">
                  {OPTS.workMode.map(o => (
                    <label key={o} className={`epf__radio ${form.workMode === o ? "epf__radio--active" : ""}`}>
                      <input 
                        type="radio" 
                        name="workMode" 
                        value={o} 
                        checked={form.workMode === o} 
                        onChange={() => set("workMode", o)} 
                      />
                      {o}
                    </label>
                  ))}
                </div>
              </F>
            </div>
            <F label="Additional Notes / Message">
              <textarea 
                className="epf__textarea" 
                rows="4" 
                value={form.additionalNotes} 
                onChange={e => set("additionalNotes", e.target.value)} 
                placeholder="Tell us why you'd like to partner with Bairees, your key achievements, or any other information..." 
              />
            </F>
          </div>
        )}

        <div className="epf__actions">
          {step > 0 && <p className="epf__btn epf__btn--ghost" onClick={back}>← Back</p>}
          {step < 2
            ? <p className="epf__btn epf__btn--primary" onClick={next}>Continue →</p>
            : <button type="submit" className="epf__btn epf__btn--primary" disabled={isSubmitting}>
                {isSubmitting ? "Submitting..." : "Submit Application 🌟"}
              </button>
          }
        </div>
      </form>
    </div>
  );
}

function F({ label, error, children }) {
  return (
    <div className="epf__field">
      <label className="epf__label">{label}</label>
      {children}
      {error && <span className="epf__error">{error}</span>}
    </div>
  );
}